import { Box, ButtonBase, Collapse, Typography, useTheme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useId, useState } from "react";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { type ReassistRequestSummary } from "../types";
import { formatDate } from "../utils/format";
import { NO_TRANSITION, sectionSx, TEXT } from "../utils/tokens";
import { AmountText } from "./AmountText";
import { ProgressStepper } from "./ProgressStepper";
import { RequestDetailBody } from "./RequestDetailBody";

interface Props {
  request: ReassistRequestSummary;
}

/** カードの内側の左右（PC 24px、SP 16px）。 */
const PX = { xs: 2, [MOBILE_BREAKPOINT]: 3 };

/**
 * 取得依頼1件のカード。白地と1px の枠、角丸8、影なし。hover と展開中は枠だけ primary.main にする。
 * PC は2列（左=住所・メタ・ステッパー、右=金額と暫定のチップ・展開アイコン）、SP は1列（住所・メタ・金額・ステッパー）。
 * 上部（住所と金額）を押すと開き、書類ごとの進捗と、納品済みの完全版（黒塗りなし）を表示する。
 * 詳細は開いたときだけ取得する（ダウンロードURLの有効期限が短いため）。
 */
export const ReassistRequestRow: React.FC<Props> = ({ request }) => {
  const theme = useTheme();
  const teal = theme.palette.primary.main;
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();
  const toggle = (): void => {
    setExpanded((value) => !value);
  };

  return (
    <Box
      sx={{
        ...sectionSx(theme),
        overflow: "hidden",
        position: "relative",
        transition: "border-color 0.2s",
        ...NO_TRANSITION,
        "&:hover": { borderColor: teal },
        ...(expanded && { borderColor: teal }),
      }}
    >
      <ButtonBase
        component="div"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={toggle}
        sx={{
          display: "grid",
          width: "100%",
          textAlign: "left",
          alignItems: "start",
          justifyContent: "stretch",
          borderRadius: "8px 8px 0 0",
          gridTemplateColumns: {
            xs: "1fr",
            [MOBILE_BREAKPOINT]: "1fr auto 20px",
          },
          gridTemplateAreas: {
            xs: '"main" "amount"',
            [MOBILE_BREAKPOINT]: '"main amount icon"',
          },
          columnGap: 2,
          px: PX,
          pt: { xs: 2, [MOBILE_BREAKPOINT]: 2.5 },
          pb: 1.5,
          // SP は展開アイコンを右上に置くため、住所の右に余白を取る
          pr: { xs: 6, [MOBILE_BREAKPOINT]: 3 },
          "&:focus-visible, &.Mui-focusVisible": {
            outline: `2px solid ${teal}`,
            outlineOffset: "-2px",
          },
        }}
      >
        <Box sx={{ gridArea: "main", minWidth: 0 }}>
          <Typography
            sx={{
              ...TEXT.cardTitle,
              color: "text.primary",
              wordBreak: "break-all",
            }}
          >
            {request.address}
          </Typography>
          {/* 見出しは住所のまま。マンション名は住所の下の補足にする（依頼時に任意で入力された場合だけ） */}
          {request.buildingName !== "" && (
            <Typography
              sx={{
                ...TEXT.body,
                color: "text.primary",
                overflowWrap: "anywhere",
              }}
            >
              {request.buildingName}
            </Typography>
          )}
          <Typography
            component="div"
            sx={{
              ...TEXT.meta,
              mt: 0.25,
              color: "text.secondary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <Box component="span" sx={visuallyHidden}>
              依頼日{" "}
            </Box>
            {formatDate(request.requestedAt)}
            {request.deliveredAt !== null && (
              <>
                <Box component="span" aria-hidden sx={{ mx: 0.75 }}>
                  ・
                </Box>
                {/* 請求は納品日の月になる。納品済みの依頼は納品日を出す */}
                <Box component="span" sx={visuallyHidden}>
                  納品日{" "}
                </Box>
                <Box component="span" aria-hidden>
                  納品
                </Box>{" "}
                {formatDate(request.deliveredAt)}
              </>
            )}
            <Box component="span" aria-hidden sx={{ mx: 0.75 }}>
              ・
            </Box>
            書類 {request.documentCount ?? request.items.length}件
          </Typography>
        </Box>
        <Box
          sx={{
            gridArea: "amount",
            justifySelf: { xs: "start", [MOBILE_BREAKPOINT]: "end" },
            mt: { xs: 1, [MOBILE_BREAKPOINT]: 0 },
            textAlign: { [MOBILE_BREAKPOINT]: "right" },
          }}
        >
          <AmountText
            amount={request.amount.total}
            taxIncluded
            isProvisional={request.amount.isProvisional}
            size="lg"
            chipBelow
          />
        </Box>
        <ExpandMoreIcon
          aria-hidden
          sx={{
            gridArea: "icon",
            position: { xs: "absolute", [MOBILE_BREAKPOINT]: "static" },
            top: { xs: 16 },
            right: { xs: 16 },
            alignSelf: "start",
            mt: { [MOBILE_BREAKPOINT]: "2px" },
            fontSize: 20,
            color: "text.secondary",
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
            ...NO_TRANSITION,
          }}
        />
      </ButtonBase>
      {/* ステッパーの範囲を押しても開閉する（キーボードは上の button で操作する） */}
      <Box
        onClick={toggle}
        sx={{
          px: PX,
          pb: { xs: 2, [MOBILE_BREAKPOINT]: 2.5 },
          cursor: "pointer",
        }}
      >
        <ProgressStepper status={request.status} />
      </Box>
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        id={bodyId}
        sx={{
          "@media (prefers-reduced-motion: reduce)": {
            transitionDuration: "0s !important",
          },
        }}
      >
        <Box
          sx={{
            px: PX,
            py: { xs: 2, [MOBILE_BREAKPOINT]: 2.5 },
            borderTop: `1px solid ${theme.palette.border.main}`,
          }}
        >
          <RequestDetailBody request={request} />
        </Box>
      </Collapse>
    </Box>
  );
};
